import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import type { User, Wallet } from '../../types/user.types';
import type { SnackbarOptions, SectionInterface } from '../../types/app.types';
import { authService } from '../../api/services/auth.service';
import { isWebBuild } from '../../utils/environment';

// ============================================================================
// ASYNC THUNKS
// ============================================================================

/**
 * Get user profile
 * Fetches user data and wallets from backend
 */
export const getUserProfile = createAsyncThunk(
  'app/getUserProfile',
  async (accessToken: string) => {
    const isWeb = isWebBuild();
    const isPlugin = !isWeb;
    
    const userData = await authService.getUserProfile(isPlugin);
    
    return {
      ...userData,
      accessToken,
    };
  }
);

/**
 * Request upgrade (increase user points)
 */
export const requestUpgrade = createAsyncThunk(
  'app/requestUpgrade',
  async () => {
    // TODO: Implement requestUpgrade API call in auth.service.ts
    // For now, return empty data
    return { wallets: [] };
  }
);

// ============================================================================
// STATE INTERFACE
// ============================================================================

export interface AppSliceState {
  // User & Authentication
  user: User | null;
  wallets: Wallet[];
  isLoggedIn: boolean;
  
  // App State
  isAppLoading: boolean;
  currentPage: string;
  previousPage: 'landing' | 'projects' | null;
  currentLang: string;
  currentApp: 'Figma' | 'FigJam' | 'Web';
  isMaximized: boolean;
  
  // UI State
  snackbarOptions: SnackbarOptions | null;
  selection: SectionInterface[];
  currentFeature: 'non-responsive' | 'responsive' | 'localize' | 'ai';
  showRatingDialog: boolean;
  
  // Onboarding
  isSkipOnBoarding: boolean;
  showOnboarding: boolean;
  isOnboardingCheck: boolean;
  onLandingFirstTime: boolean;
  
  // Projects
  currentProjectId: string | null;
  showProjectDetailsDrawer: boolean;
  showFirstProjectDialog: boolean;
}

const initialState: AppSliceState = {
  // User & Authentication
  user: null,
  wallets: [],
  isLoggedIn: false,
  
  // App State
  isAppLoading: true,
  currentPage: '/onboarding',
  previousPage: null,
  currentLang: 'en',
  currentApp: 'Web',
  isMaximized: false,
  
  // UI State
  snackbarOptions: null,
  selection: [],
  currentFeature: 'ai',
  showRatingDialog: false,
  
  // Onboarding
  isSkipOnBoarding: false,
  showOnboarding: false,
  isOnboardingCheck: false,
  onLandingFirstTime: false,
  
  // Projects
  currentProjectId: null,
  showProjectDetailsDrawer: false,
  showFirstProjectDialog: false,
};

const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    // User & Authentication
    setUser: (state, action: PayloadAction<User | null>) => {
      state.user = action.payload;
      state.isLoggedIn = action.payload !== null;
    },
    setUserPartial: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      } else {
        state.user = action.payload as User;
      }
    },
    setLoginState: (state, action: PayloadAction<boolean>) => {
      state.isLoggedIn = action.payload;
    },
    setWallets: (state, action: PayloadAction<Wallet[]>) => {
      state.wallets = action.payload;
    },
    cleanUserActions: (state) => {
      if (state.user?.metadata?.userActions) {
        state.user.metadata.userActions = [];
      }
    },
    logout: (state) => {
      state.user = null;
      state.wallets = [];
      state.isLoggedIn = false;
      state.currentProjectId = null;
      state.currentPage = '/';
      state.isAppLoading = false;
    },
    
    // App State
    setAppLoading: (state, action: PayloadAction<boolean>) => {
      state.isAppLoading = action.payload;
    },
    setCurrentPage: (state, action: PayloadAction<string>) => {
      state.currentPage = action.payload;
    },
    setPreviousPage: (state, action: PayloadAction<'landing' | 'projects' | null>) => {
      state.previousPage = action.payload;
    },
    setCurrentLang: (state, action: PayloadAction<string>) => {
      state.currentLang = action.payload;
    },
    setCurrentApp: (state, action: PayloadAction<'Figma' | 'FigJam' | 'Web'>) => {
      state.currentApp = action.payload;
    },
    setMaximized: (state, action: PayloadAction<boolean>) => {
      state.isMaximized = action.payload;
    },
    
    // UI State
    setSnackbar: (state, action: PayloadAction<SnackbarOptions | null>) => {
      state.snackbarOptions = action.payload;
    },
    setSelection: (state, action: PayloadAction<SectionInterface[]>) => {
      state.selection = action.payload;
    },
    setCurrentFeature: (state, action: PayloadAction<'non-responsive' | 'responsive' | 'localize' | 'ai'>) => {
      state.currentFeature = action.payload;
    },
    setShowRatingDialog: (state, action: PayloadAction<boolean>) => {
      state.showRatingDialog = action.payload;
    },
    
    // Onboarding
    setIsSkipOnBoarding: (state, action: PayloadAction<boolean>) => {
      state.isSkipOnBoarding = action.payload;
    },
    setShowOnboarding: (state, action: PayloadAction<boolean>) => {
      state.showOnboarding = action.payload;
    },
    setOnboardingCheck: (state, action: PayloadAction<boolean>) => {
      state.isOnboardingCheck = action.payload;
    },
    setOnLandingFirstTime: (state, action: PayloadAction<boolean>) => {
      state.onLandingFirstTime = action.payload;
    },
    
    // Projects
    setCurrentProjectId: (state, action: PayloadAction<string | null>) => {
      state.currentProjectId = action.payload;
    },
    setShowProjectDetailsDrawer: (state, action: PayloadAction<boolean>) => {
      state.showProjectDetailsDrawer = action.payload;
    },
    setShowFirstProjectDialog: (state, action: PayloadAction<boolean>) => {
      state.showFirstProjectDialog = action.payload;
    },
  },
  extraReducers: (builder) => {
    // getUserProfile
    builder
      .addCase(getUserProfile.pending, (state) => {
        
      })
      .addCase(getUserProfile.fulfilled, (state, action) => {
        const { accessToken, wallets, planDetails, ...userData } = action.payload;

        // Map backend user format to User type
        if (userData) {
          const user: User = {
            id: userData.id,
            name: userData.full_name || userData.name,
            email: userData.email,
            avatar: userData.avatar,
            provider: userData.provider, // Include provider field (e.g., 'Google', 'email')
            createdAt: userData.created_at || new Date().toISOString(),
            updatedAt: userData.updated_at || new Date().toISOString(),
            metadata: userData.metadata,
          };
          state.user = user;
          state.isLoggedIn = true;
        }

        // Save planDetails if present
        if (planDetails) {
          (state as any).planDetails = planDetails;
        }

        // Update wallets if provided
        if (wallets && Array.isArray(wallets)) {
          state.wallets = wallets;
        }
        
        // Check if onboarding should be shown based on metadata.isOnBoardingShown flag
        // Get isOnBoardingShown from metadata (default to false if not set, meaning onboarding not shown yet)
        // Check both metadata.isOnBoardingShown and metadata.is_onboarding_shown (in case API uses snake_case)
        // Also handle string values like "false" or "true"
        const isOnBoardingShownRaw = userData?.metadata?.isOnBoardingShown ?? 
                                      userData?.metadata?.is_onboarding_shown ?? 
                                      false;
        // Convert to boolean if it's a string
        const isOnBoardingShown = typeof isOnBoardingShownRaw === 'string' 
          ? isOnBoardingShownRaw.toLowerCase() === 'true'
          : Boolean(isOnBoardingShownRaw);
        
        // Check if current page is landing (works for both web routes '/' or '/landing' and plugin 'landing')
        // For web, prioritize window.location.pathname check (more reliable than state.currentPage)
        let isOnLandingPage = false;
        
        // For web, check actual window location first (most reliable)
        if (typeof window !== 'undefined' && window.location) {
          const pathname = window.location.pathname;
          // Don't treat as landing page if user is on conversation page
          isOnLandingPage = pathname !== '/conversation' && (pathname === '/' || pathname === '/landing');
        }
        
        // Also check currentPage state (works for plugin or as fallback)
        if (!isOnLandingPage) {
          isOnLandingPage = state.currentPage === '/' || 
                           state.currentPage === '/landing' || 
                           state.currentPage === 'landing';
        }
        
        console.log('getUserProfile.fulfilled - onboarding check:', {
          isOnBoardingShown,
          isOnLandingPage,
          currentPage: state.currentPage,
          pathname: typeof window !== 'undefined' ? window.location?.pathname : 'N/A',
          metadata: userData?.metadata,
          metadataKeys: userData?.metadata ? Object.keys(userData.metadata) : [],
        });
        if(userData?.metadata?.hasCreatedProject) {
          state.showFirstProjectDialog = false;
        } else {
          state.showFirstProjectDialog = true;
        }
        // If onboarding not shown and user is on landing page, show onboarding
        if (isOnBoardingShown === false && isOnLandingPage) {
          console.log('Setting showOnboarding to true - user is on landing page and onboarding not shown');
          state.showOnboarding = true;
        } else if (isOnBoardingShown === false) {
          console.log('User is not on landing page but isOnBoardingShown is false - will update flag in validateToken');
        }
        
        state.isAppLoading = false;
      })
      .addCase(getUserProfile.rejected, (state) => {
        state.isAppLoading = false;
      })
      
      // requestUpgrade
      .addCase(requestUpgrade.pending, (state) => {
        // Optionally set loading state
      })
      .addCase(requestUpgrade.fulfilled, (state, action) => {
        const { wallets } = action.payload;
        if (wallets && Array.isArray(wallets)) {
          state.wallets = wallets;
        }
      })
      .addCase(requestUpgrade.rejected, (state) => {
        // Handle error
      });
  },
});

export const {
  // User & Authentication
  setUser,
  setUserPartial,
  setLoginState,
  setWallets,
  cleanUserActions,
  logout,
  
  // App State
  setAppLoading,
  setCurrentPage,
  setPreviousPage,
  setCurrentLang,
  setCurrentApp,
  setMaximized,
  
  // UI State
  setSnackbar,
  setSelection,
  setCurrentFeature,
  setShowRatingDialog,
  
  // Onboarding
  setIsSkipOnBoarding,
  setShowOnboarding,
  setOnboardingCheck,
  setOnLandingFirstTime,
  
  // Projects
  setCurrentProjectId,
  setShowProjectDetailsDrawer,
  setShowFirstProjectDialog,
} = appSlice.actions;

/// Selectors - User & Authentication (with defensive checks)
export const selectUser = (state: { app: AppSliceState }) => state.app?.user ?? null;
export const selectWallets = (state: { app: AppSliceState }) => state.app?.wallets ?? [];
export const selectIsLoggedIn = (state: { app: AppSliceState }) => state.app?.isLoggedIn ?? false;

// Selectors - App State (with defensive checks)
export const selectIsAppLoading = (state: { app: AppSliceState }) => state.app?.isAppLoading ?? true;
export const selectCurrentPage = (state: { app: AppSliceState }) => state.app?.currentPage ?? null;
export const selectPreviousPage = (state: { app: AppSliceState }) => state.app?.previousPage ?? null;
export const selectCurrentLang = (state: { app: AppSliceState }) => state.app?.currentLang ?? 'en';
export const selectCurrentApp = (state: { app: AppSliceState }) => state.app?.currentApp ?? null;
export const selectIsMaximized = (state: { app: AppSliceState }) => state.app?.isMaximized ?? false;

// Selectors - UI State (with defensive checks)
export const selectSnackbarOptions = (state: { app: AppSliceState }) => state.app?.snackbarOptions ?? null;
export const selectSelection = (state: { app: AppSliceState }) => state.app?.selection ?? null;
export const selectCurrentFeature = (state: { app: AppSliceState }) => state.app?.currentFeature ?? null;
export const selectShowRatingDialog = (state: { app: AppSliceState }) => state.app?.showRatingDialog ?? false;

// Selectors - Onboarding (with defensive checks)
export const selectIsSkipOnBoarding = (state: { app: AppSliceState }) => state.app?.isSkipOnBoarding ?? false;
export const selectShowOnboarding = (state: { app: AppSliceState }) => state.app?.showOnboarding ?? false;
export const selectIsOnboardingCheck = (state: { app: AppSliceState }) => state.app?.isOnboardingCheck ?? false;
export const selectOnLandingFirstTime = (state: { app: AppSliceState }) => state.app?.onLandingFirstTime ?? false;

// Selectors - Projects (with defensive checks)
export const selectCurrentProjectId = (state: { app: AppSliceState }) => state.app?.currentProjectId ?? null;
export const selectShowProjectDetailsDrawer = (state: { app: AppSliceState }) => state.app?.showProjectDetailsDrawer ?? false;
export const selectShowFirstProjectDialog = (state: { app: AppSliceState }) => state.app?.showFirstProjectDialog ?? false;

export default appSlice.reducer;

